import { useState, useEffect } from "react";

interface TimerProps {
  endAt: string;
  onComplete?: () => void;
  compact?: boolean;
}

export function Timer({ endAt, onComplete, compact }: TimerProps) {
  const [remaining, setRemaining] = useState(() => calcRemaining(endAt));

  useEffect(() => {
    const interval = setInterval(() => {
      const r = calcRemaining(endAt);
      setRemaining(r);
      if (r.total <= 0) {
        clearInterval(interval);
        onComplete?.();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [endAt, onComplete]);

  if (remaining.total <= 0) return <span className="timer done">Done!</span>;

  const parts: string[] = [];
  if (remaining.hours > 0) parts.push(`${remaining.hours}h`);
  if (remaining.minutes > 0) parts.push(`${remaining.minutes}m`);
  parts.push(`${remaining.seconds}s`);

  const text = parts.join(" ");
  const pct = remaining.total > 0
    ? Math.max(0, Math.min(100, 100 - (remaining.total / (remaining.total + (Date.now() - new Date(endAt).getTime() + remaining.total)) * 100)))
    : 100;

  return (
    <span className="timer" style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      fontFamily: "var(--font-body)",
      fontWeight: 600,
      fontSize: compact ? "0.8rem" : "0.9rem",
      color: "var(--terra-dark)",
      fontVariantNumeric: "tabular-nums",
    }}>
      <span style={{
        display: "inline-block",
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        background: "var(--terra)",
        animation: "pulse 1.5s infinite",
      }} />
      {text}
    </span>
  );
}

function calcRemaining(endAt: string) {
  const diff = new Date(endAt).getTime() - Date.now();
  if (diff <= 0) return { total: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    total: diff,
    hours: Math.floor(diff / 3_600_000),
    minutes: Math.floor((diff % 3_600_000) / 60_000),
    seconds: Math.floor((diff % 60_000) / 1_000),
  };
}
